import React, { useEffect, useState, useContext, useMemo } from 'react';
import API from '@aws-amplify/api';
import { shopifyContext } from 'contexts/ShopifyContext';
import LoadingPanel from 'components/Progress/LoadingPanel';
import throttle from 'lodash/throttle';
import DebugContainer from 'components/Debug/DebugContainer';
import List from '@mui/material/List';
import ListItemText from '@mui/material/ListItemText';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import { Link as RouterLink } from 'react-router-dom';
import Button from '@mui/material/Button';
import { useHistory } from 'react-router-dom';
import SearchCollections from 'components/Search/SearchCollections';
import NoSearchResult from 'components/Search/NoResultDisplay';
import { cgtag } from 'utilities/gtag';
import { PRODUCT_HANDLE } from 'utilities/constants';
import { useProduct } from 'effects/useProduct';

interface QuickSearchResultsProps {
  query: string;
  onClose?: () => void;
}

const QuickSearchResults: React.FC<QuickSearchResultsProps> = ({ query, onClose }) => {
  const { predictiveSearch, getProductTags } = useContext(shopifyContext);
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<any>(false);
  const [productTags, setProductTags] = useState<string[]>();
  const [popularSearches, setPopularSearches] = useState<string[]>();
  const throttledHandler = useMemo(() => throttle(setSearchQuery, 300), []);
  const history = useHistory();

  const { product: cardDotGiftCard } = useProduct(PRODUCT_HANDLE.CARD_GIFT);

  useEffect(() => {
    // Throttle updating the search query...
    if (query && query.length) {
      throttledHandler(query);
    }
  }, [query, throttledHandler]);

  useEffect(() => {
    if (searchQuery && searchQuery.length >= 3) {
      // Run a search
      setSearching(true);
      const searchRequest = predictiveSearch(searchQuery);
      searchRequest.then((result: any) => {
        // console.debug('gotResults', searchRequest, searchQuery, result);
        cgtag('search', {
          search_term: searchQuery,
        });
        setSearchResults(result.data);
        setSearching(false);
      });
      return () => {
        // console.debug('cancelledSearch', searchQuery);
        API.cancel(searchRequest);
      };
    }
  }, [searchQuery, predictiveSearch]);

  useEffect(() => {
    const productTags = getProductTags();
    productTags.then((result: string[]) => {
      setProductTags(result);
    });
  }, [getProductTags]);

  useEffect(() => {
    setPopularSearches(productTags?.filter((item) => item.toLowerCase().includes(searchQuery.toLowerCase())));
  }, [searchQuery, productTags]);

  const haveResult = searchResults && (!!searchResults?.collections?.length || !!searchResults?.products?.length);

  const directToFullSearchResult = () => {
    cgtag('select_content', {
      content_type: 'searchAll',
      item_id: searchQuery,
    });
    onClose && onClose();
    history.push({ pathname: '/search', search: `?query=${searchQuery}` });
  };

  return (
    <Box sx={{ pt: 4, px: 2 }}>
      {searching && <LoadingPanel />}
      {!searching && searchResults && !haveResult && <NoSearchResult searchQuery={searchQuery} source="quickSearch" alternative={cardDotGiftCard} onClose={onClose} />}
      {!searching && searchResults && haveResult && (
        <>
          {popularSearches && !!popularSearches.length && (
            <>
              <Box sx={{ pl: { xs: 0, sm: 2 }, typography: 'subtitle1', color: 'text.secondary' }}>Popular Searches</Box>
              <List sx={{ mb: 4 }}>
                {popularSearches.slice(0, 3).map((item: string, idx: number) => (
                  <ListItem
                    key={idx}
                    sx={{
                      py: 1,
                      px: { xs: 0, sm: 2 },
                    }}
                    button
                    // component={RouterLink}
                    // to={'/product/' + item}
                    onClick={() => {
                      cgtag('select_content', {
                        content_type: 'searchPopular',
                        item_id: item,
                      });
                      if (onClose) {
                        console.debug('closing...');
                        onClose && onClose();
                      }
                      history.push({ pathname: '/search', search: `?query=${item}` });
                    }}
                  >
                    <ListItemText
                      primary={item}
                      primaryTypographyProps={{
                        variant: 'subtitle2',
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </>
          )}
          {searchResults && searchResults.products && !!searchResults.products.length && (
            <SearchCollections collections={searchResults.products.slice(0, 3)} title="Gift Cards" showPlaceholder={true} onClose={onClose} />
          )}
          {searchResults && searchResults.collections && !!searchResults.collections.length && (
            <>
              <Box sx={{ pl: { xs: 0, sm: 2 }, typography: 'subtitle1', color: 'text.secondary' }}>Collections</Box>
              <List sx={{ mb: 4 }}>
                {searchResults.collections.slice(0, 3).map((collection: any, idx: number) => (
                  <ListItem
                    key={idx}
                    sx={{
                      py: 1,
                      px: { xs: 0, sm: 2 },
                    }}
                    button
                    component={RouterLink}
                    to={'/landing/' + collection.handle}
                    onClick={() => {
                      cgtag('select_content', {
                        content_type: 'collection',
                        item_id: collection.handle,
                      });
                      if (onClose) onClose();
                    }}
                  >
                    {false && collection.featured_image && (
                      <ListItemAvatar>
                        <Avatar variant="square" alt={collection.title} src={collection.featured_image.url} />
                      </ListItemAvatar>
                    )}
                    <ListItemText
                      primary={collection.title}
                      primaryTypographyProps={{
                        variant: 'subtitle2',
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </>
          )}
          <Box sx={{ pt: 2, pb: 5 }} display="flex" justifyContent="center">
            <Button onClick={directToFullSearchResult} variant="outlined" size="large">
              See all results
            </Button>
          </Box>
        </>
      )}
      <DebugContainer sx={{ p: 2 }} data={{ searchQuery, searchResults }} />
    </Box>
  );
};

export default QuickSearchResults;
